import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getFooterLine } from '../lib/footerLines.js';
import { pickGamePromo } from '../lib/gamePromo.js';

/**
 * NerdStackFooter — the small footer line at the bottom of every page.
 * Rotates between ~20 lines per page load. Rarely (~1 in 5000) shows
 * the "golden footer" easter egg.
 *
 * Props:
 *   variant: 'landing' (default) | 'tool'
 *     - 'landing': full-width footer with copyright on the left, line on the right
 *     - 'tool': compact, centered, for the bottom of individual tool pages
 *   muted (color): override the default muted color
 *   family (V42.1, optional): 'chem'|'phys'|'math'|'bio'|'tools'|'games'|'music'|'history'
 *     - if passed, renders a game-promo line above the rotation line.
 *       Family-weighted: a chem-tool footer mostly promotes chem games,
 *       occasionally cross-family. Pages that don't pass family get
 *       the existing footer unchanged.
 *   excludeGameId (V42.1, optional): hide a specific game from the
 *     promo pick (use this from inside a game page so it doesn't
 *     promote itself).
 */
export function NerdStackFooter({ variant = 'landing', muted = '#94A3B8', family, excludeGameId }) {
  // useMemo with empty deps → picks once per component mount (= once per page load),
  // never re-rolls on re-renders. This is the right behavior — we want stability
  // within a single visit, freshness across visits.
  const line = useMemo(() => getFooterLine(), []);
  // Same stability rule for the game promo — one pick per page load.
  const promo = useMemo(() => (family ? pickGamePromo(family, excludeGameId) : null), [family, excludeGameId]);

  const lineStyle = {
    color: line.golden ? '#FCD34D' : muted,
    fontWeight: line.golden ? 600 : 400,
    fontSize: 13,
    letterSpacing: line.golden ? 0.2 : 0,
    // Tiny shimmer for the golden footer (CSS animation defined below)
    animation: line.golden ? 'goldenShimmer 3s ease-in-out infinite' : 'none',
  };

  if (variant === 'tool') {
    return (
      <>
        <div style={{
          marginTop: 32,
          textAlign: 'center',
          padding: '16px 12px',
        }}>
          {promo && (
            <div style={{ marginBottom: 12 }}>
              <Link
                to={promo.path}
                style={{
                  color: '#A78BFA',
                  textDecoration: 'none',
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                → Try {promo.name}: <span style={{ color: muted, fontWeight: 400 }}>{promo.tagline}</span>
              </Link>
            </div>
          )}
          <span style={lineStyle}>{line.text}</span>
          <div style={{ marginTop: 8 }}>
            <Link to="/legal" style={{ color: muted, textDecoration: 'none', fontSize: 12 }}>
              The boring but necessary
            </Link>
          </div>
        </div>
        {line.golden && <GoldenShimmerKeyframes />}
      </>
    );
  }

  // Landing variant
  return (
    <>
      <footer style={{
        marginTop: 34,
        paddingTop: 32,
        borderTop: '1px solid #262A3F',
        fontSize: 13,
        color: muted,
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
      }} className="nerdstack-landing-footer">
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }} className="nerdstack-landing-footer-left">
          <span>© {new Date().getFullYear()} NerdSuite</span>
          <Link to="/legal" style={{ color: muted, textDecoration: 'none', fontSize: 12.5 }}>
            The boring but necessary
          </Link>
        </div>
        <div style={lineStyle}>{line.text}</div>
      </footer>
      {/* V60.1: on narrow viewports the footer wraps onto two rows
          (left group + rotating text). At full width they sit at
          opposite edges, which looks fine. On phone they look awkward
          left-aligned — center the wrap. */}
      <style>{`
        @media (max-width: 640px) {
          .nerdstack-landing-footer {
            justify-content: center !important;
            text-align: center;
          }
          .nerdstack-landing-footer-left {
            justify-content: center;
            width: 100%;
          }
          .nerdstack-landing-footer > div:last-child {
            width: 100%;
            justify-content: center !important;
            text-align: center;
          }
        }
      `}</style>
      {line.golden && <GoldenShimmerKeyframes />}
    </>
  );
}

// Subtle shimmer keyframes, only injected when a golden footer renders.
// Animates color slightly between gold and a paler gold — quiet enough that
// it doesn't scream "AD!", just enough that you notice something's different.
function GoldenShimmerKeyframes() {
  return (
    <style>{`
      @keyframes goldenShimmer {
        0%, 100% { color: #FCD34D; }
        50%      { color: #FBBF24; }
      }
    `}</style>
  );
}
